import { postCustomerOrders } from '@lib/hooks/useCustomerOrders'
import s from './AddKit.module.css'

import { useTranslations } from 'next-intl'
import React, { ChangeEvent, useState } from 'react'
import { ArrowRight, Plus } from '@components/icons'

function AddKit() {
  const t = useTranslations('Account')
  const [ isAddKitButtonActive, setIsAddKitButtonActive ] = useState(false);
  const [ retailActivationCode, setRetailActivationCode ] = useState<string | undefined>(undefined);
  const [ isAddingKit, setIsAddingKit ] = useState<boolean>(false);
  const [ message, setMessage ] = useState<string | undefined>(undefined);
  const [ isSuccessShowing, setIsSuccessShowing ] = useState<boolean>(false);

  const addKitInputRef = React.createRef<HTMLInputElement>();

  const onAddKitClick = () => {
    if (isAddKitButtonActive) return;

    setIsAddKitButtonActive(true);
    addKitInputRef?.current?.focus();
  }

  const handleRetailActivationCodeChange = (e: React.FormEvent<HTMLInputElement>) => {
    setRetailActivationCode(e.currentTarget.value);
  }

  const handleAddKitFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (retailActivationCode == null || retailActivationCode.trim().length === 0) return;

    setMessage(undefined);
    setIsAddingKit(true);
    postCustomerOrders({
      activationID: retailActivationCode
    })
    .then(response => {
      if (!response.ok) {
        response.json()
        .then(errorBody => {
          if ((errorBody?.error as string)?.toLowerCase() === 'invalidtestid') {
            setMessage(`${t('addKitErrorInvalidTestID')}`);
          } else if ((errorBody?.error as string)?.toLowerCase() === 'noproductfortestid') {
            setMessage(`${t('addKitErrorNoProductForTestID')}`);
          } else if ((errorBody?.error as string)?.toLowerCase() === 'productisnotretail') {
            setMessage(`${t('addKitErrorProductIsNotRetail')}`);
          } else {
            setMessage(`${t('addKitError')} (${response.status} - ${response.statusText})`);
          }
          setIsAddingKit(false);
        })
        .catch(_ => {
          setMessage(`${t('addKitError')} (${response.status} - ${response.statusText})`);
          setIsAddingKit(false);
        })
      } else {
        setIsAddKitButtonActive(false);
        setRetailActivationCode(undefined);
        setIsAddingKit(false);

        setIsSuccessShowing(true);
        setTimeout(() => setIsSuccessShowing(false), 2000);
      }
    })
    .catch(error => {
      console.error(error);
      setMessage(`${t('addKitError')}`);
      setIsAddingKit(false);
    });
  }

  return (
    <>
      <div
        className={`${s.wFitContent} ${isAddKitButtonActive ? '' : s.clickable} flex flex-col items-center bg-primary100 rounded-3xl p-8 relative overflow-hidden`}
        onClick={onAddKitClick}>
        <div className={`${s.addKitButton} ${isAddKitButtonActive ? s.active : ''} overflow-hidden relative rounded-full border border-dashed border-tertiary500`}>
          <div className={`${s.addKitIcon} absolute inset-0 flex justify-center items-center`}>
            <Plus className='text-tertiary500' />
          </div>
          <form
            className={`${s.addKitInput} absolute inset-0 flex justify-center items-center`}
            onSubmit={handleAddKitFormSubmit}>
            <div className='CustomInput rounded-cornerL h-e61 sm:h-e72 flex'>
              <div className={`${s.formInputWrapper} flex-1`}>
                <input
                  ref={addKitInputRef}
                  placeholder={t('activationCode')}
                  id='retailActivationCode'
                  autoComplete='none'
                  className={`${s.input} text-tertiary500`}
                  onChange={handleRetailActivationCodeChange}
                />
                <label
                  htmlFor='retailActivationCode'
                  className='whitespace-nowrap text-button'>{t('activationCode')}</label>
              </div>
            </div>

            <button
              className={`${s.addKitOkButton} rounded-full flex justify-center items-center ${retailActivationCode == null || retailActivationCode.trim().length === 0 || isAddingKit ? s.disabled : ''}`}
              type='submit'
              disabled={retailActivationCode == null || retailActivationCode.trim().length === 0 || isAddingKit}>
              <ArrowRight className='text-tertiary500' />
            </button>
          </form>
        </div>
        {message && (
          <div className="text-error500 bg-white border border-primary500 p-3 mt-2">
            {message}
          </div>
        )}
        <div className='flex-1 mt-4 text-center'>
          <p className='text-paragraph02 font-medium'>
            {t('addRetailKitHeading')}
          </p>
          <p className='text-paragraph02 mt-2'>
            {t('addRetailKitDescription1')}
            <br />
            {t('addRetailKitDescription2')}
          </p>
        </div>

        {isSuccessShowing &&
          <div
            id="box"
            className='text-center bg-tertiary500 absolute bottom-0 left-0 h-full w-full z-20 flex flex-col justify-center'
          >
            <svg
              className={s.checkmark}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 52 52"
            >
              {' '}
              <circle
                className={s.checkmark__circle}
                cx="26"
                cy="26"
                r="25"
                fill="none"
              />{' '}
              <path
                className={s.checkmark__check}
                fill="none"
                d="M14.1 27.2l7.1 7.2 16.7-16.8"
              />
            </svg>
            <h2 className="text-primary500 text-3xl">
              {t('kitSuccessfullyAdded')}
            </h2>
          </div>
        }
      </div>
    </>
  )
}

export default AddKit
